import { onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useDeviceStore } from '@/stores/device'
import { storeToRefs } from 'pinia'

/**
 * 在离开 base（底座）路由页面时，自动恢复暂存的子设备为 currentDevice。
 * 仅当回首页时才恢复，离开 base 时清除不再有效的 pendingChildDevice。
 *
 * 使用 router.afterEach 而非 onBeforeRouteLeave 处理，
 * 避免与其他 onBeforeRouteLeave guard（如 useUploadConfirmOnLeave）冲突
 * 导致 next(callback) 被覆盖不执行。
 *
 * 在 base 的每个子页面（pages/player/light/settings）的 setup 中调用一次即可。
 */
export function useRestoreChildDevice() {
  const deviceStore = useDeviceStore()
  const { pendingChildDevice, currentDevice } = storeToRefs(deviceStore)
  const router = useRouter()

  const unregister = router.afterEach((to, from) => {
    // 仅处理离开 base 路由的导航
    if (from.meta.type !== 'base') return

    // 条件1：有暂存的子设备
    const hasPendingChildDevice = !!pendingChildDevice.value
    // 条件2：回首页
    const isGoingHome = to.name === 'home' || to.path === '/'
    // 条件3：pendingChildDevice 确实是当前 currentDevice（底座）的子设备
    const isValidChild =
      hasPendingChildDevice &&
      currentDevice.value?.subDevices?.some((s) => s.id === pendingChildDevice.value!.id)

    if (hasPendingChildDevice && !isValidChild) {
      // pendingChildDevice 已不是当前设备的子设备，清空
      pendingChildDevice.value = null
    } else if (hasPendingChildDevice && isValidChild && isGoingHome) {
      // 回首页时恢复子设备
      const child = pendingChildDevice.value!
      pendingChildDevice.value = null
      deviceStore.selectDevice(child)
    }
  })

  onUnmounted(() => {
    unregister()
  })
}
